import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, alpha, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { FilePdfOutlined } from '@ant-design/icons';
import AnimateButton from 'components/@extended/AnimateButton';
import { PageUrl } from 'const/page-url';
import { SECTION_PY } from 'const/section';

// ==============================|| CONTACT SECTION ||============================== //
// ページ最下部のCTA。FV・シミュレーション直後とあわせて計3箇所に導線を置いている。
// いずれの箇所も行き先は1つだけ。相談・問い合わせの導線を併記すると
// 「まず何をすればよいか」が分かれてしまうため、CTAは一本に絞っている。
//
// to で行き先を差し替えられる。既定は資料請求ページ。
// ブログ(/blog 配下)からはトップページ('/')へ送っている。
// 記事から来た人はサービスをまだ知らないため、フォームだけの画面ではなく
// 説明のあるトップに着地させたほうが通じるという判断(sections/blog/ArticleCta.tsx と同じ考え)。

const ContactSection = ({ to = PageUrl.DocumentRequest.urlWithSlash }: { to?: string }) => {
    const primaryColor = '#2aa1af';

    return (
        <Box
            component="section"
            id="contact"
            sx={{
                py: SECTION_PY,
                position: 'relative',
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${primaryColor} 0%, #3bbcc9 100%)`
            }}
        >
            {/* 背景装飾 */}
            <Box
                aria-hidden="true"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: `radial-gradient(at 75% 20%, ${alpha('#fff', 0.22)} 0%, transparent 60%)`,
                    pointerEvents: 'none'
                }}
            />

            <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.35 }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography
                            variant="caption"
                            component="span"
                            sx={{ color: alpha('#fff', 0.85), fontWeight: 900, letterSpacing: '0.2em', display: 'block', mb: 2.5 }}
                        >
                            DOCUMENT
                        </Typography>
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '1.85rem', md: '2.7rem' },
                                color: '#fff',
                                lineHeight: 1.4,
                                mb: 3,
                                textShadow: `0 2px 12px ${alpha('#000', 0.12)}`
                            }}
                        >
                            まずは、サービス紹介資料を<br />
                            ご覧ください。
                        </Typography>
                        <Typography
                            component="p"
                            sx={{ color: alpha('#fff', 0.92), fontSize: { xs: '0.95rem', md: '1.08rem' }, lineHeight: 1.9, mb: 5 }}
                        >
                            機能の一覧、料金、導入までの流れをまとめてご確認いただけます。<br />
                            1ヶ月の無料トライアルもご用意していますので、ご検討中の段階でもお気軽にどうぞ。
                        </Typography>

                        <AnimateButton>
                            <Button
                                component={RouterLink}
                                to={to}
                                size="large"
                                variant="contained"
                                startIcon={<FilePdfOutlined aria-hidden="true" />}
                                sx={{
                                    bgcolor: '#fff',
                                    color: primaryColor,
                                    px: 4.5,
                                    py: 1.8,
                                    borderRadius: '16px',
                                    fontSize: '1.02rem',
                                    fontWeight: 800,
                                    boxShadow: `0 12px 28px ${alpha('#000', 0.16)}`,
                                    '&:hover': { bgcolor: '#f7fbfb' }
                                }}
                            >
                                資料を請求する
                            </Button>
                        </AnimateButton>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
};

export default ContactSection;
